'use client'

import Link from 'next/link'
import { usePathname, useRouter } from 'next/navigation'
import { useContext, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useChromeT } from '../../app/i18n/useChromeT'
import { getPathLocale, localizedPath } from '../../app/i18n/supported-locales'
import { BsChatDotsFill, BsGridFill, BsRocketTakeoffFill } from 'react-icons/bs'
import {
  FaBars,
  FaBookOpen,
  FaChevronDown,
  FaChevronUp,
  FaCode,
  FaCog,
  FaCrown,
  FaDiscord,
  FaGem,
  FaHome,
  FaMagic,
  FaSearch,
  FaUser,
} from 'react-icons/fa'
import { HiMail } from 'react-icons/hi'
import BannedBanner from '../../component/BannedBanner'
import DiscountBanner from '../../component/DiscountBanner'
import DisputeBanner from '../../component/DisputeBanner'
import GemButton from '../../component/GemButton'
import ProfileDrawer from '../../component/ProfileDrawer'
import GemsDiscountBanner from '../../component/GemsDiscountBanner'
import LanguageSwitcher from '../../component/LanguageSwitcher'
import { useAuth } from '../../contexts/AuthContext'
import { isSafeCountry } from '../../helpers/helpers'
import { safeGetItem, safeSetItem } from '../../helpers/safeStorage'
import { ImageContext } from '../../contexts/ImageContext'
import { useActiveOffer, useOfferVariant } from '../../hooks/useOfferExperiment'

// ✨ NEW: exit intent + your Modal
import { useExitIntent } from 'use-exit-intent'
import Modal from '../../component/Modal'
import PopUp from '../../component/PopUp'
import DeclinedBanner from '../../component/DeclinedBanner'
import AgeGateBanner from '../../component/AgeGateBanner'

const Header = () => {
  const { i18n } = useTranslation()
  const t = useChromeT()
  const dropdownRef = useRef(null)

  const pathname = usePathname()

  // "New" bubble on the Chat button: Stories live in Discover (2026-10-05), so the nudge points
  // there until this browser has opened /chat once. Read after mount (storage is per browser and
  // the server render has none); never in safe mode or a safe country, where Stories are hidden.
  const [chatNewSeen, setChatNewSeen] = useState(true)
  useEffect(() => {
    if (pathname?.startsWith('/chat')) {
      safeSetItem('storiesChatNewSeen', '1')
      setChatNewSeen(true)
    } else setChatNewSeen(safeGetItem('storiesChatNewSeen') === '1')
  }, [pathname])
  const router = useRouter()
  const {
    user,
    loading,
    authHint,
    displayPlan,
    isPlusOrHigher,
    banned,
    safe,
    disputeBanned,
    centroDecline,
    userDataLoaded,
    ageGate,
    username,
    displayProfilePicture,
    email,
  } = useAuth()
  const showChatNew = !chatNewSeen && safe === false && !isSafeCountry()

  // Optimistically treat a returning (cookie-hinted) visitor as authed while Firebase is
  // still resolving, so the header shows the account shell instead of flashing "Sign Up".
  // Once auth resolves, `user` is the source of truth (a stale hint falls back to signed-out).
  const showAuthed = Boolean(user || (authHint && loading))
  const { setActiveButton } = useContext(ImageContext)

  const [drawerOpen, setDrawerOpen] = useState(false)
  // Large-screen (lg+) account dropdown. Smaller screens use the slide-in
  // ProfileDrawer instead (opened from the same avatar via setDrawerOpen).
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [searchInput, setSearchInput] = useState('')
  const [isMoreContentVisible, setIsMoreContentVisible] = useState(false)

  // ✨ NEW: control exit-intent modal visibility
  const [exitModalOpen, setExitModalOpen] = useState(false)

  // Exit-intent sign-up popup. Tuned to fire only on genuine "about to leave"
  // signals rather than on brief inactivity (see desktop/mobile notes below).
  const { registerHandler, unsubscribe } = useExitIntent({
    cookie: { daysToExpire: 30, key: 'popUpTriggered' },
    desktop: {
      // Fire on real exit intent (cursor leaving toward the browser chrome),
      // not on mere inactivity.
      triggerOnIdle: false,
      useBeforeUnload: false,
      triggerOnMouseLeave: true,
      // Only arm the mouse-leave listener after the visitor has spent time on
      // the page, so it can't fire during the first moments of the visit.
      mouseLeaveDelayInSeconds: 10,
    },
    mobile: {
      // Mobile has no cursor, so idle is the only signal — wait long enough
      // that it reads as "disengaged / leaving" rather than a brief pause.
      triggerOnIdle: true,
      delayInSecondsToTrigger: 30,
    },
  })

  useEffect(() => {
    // If the user IS logged in, remove any handler and close the modal.
    if (user) {
      try {
        unsubscribe && unsubscribe('open-exit-intent-modal')
      } catch {
        // no-op for older versions
      }
      if (exitModalOpen) setExitModalOpen(false)
      return
    }

    // Only register the handler when NOT authenticated.
    const id = 'open-exit-intent-modal'
    registerHandler({
      id,
      handler: () => {
        // extra safety: don't show if the user became authed meanwhile
        if (user) return
        setExitModalOpen(true)
      },
    })

    // Cleanup on unmount or when auth state changes
    return () => {
      try {
        unsubscribe && unsubscribe(id)
      } catch {
        // no-op for older versions
      }
    }
  }, [user, registerHandler, unsubscribe, exitModalOpen])

  // `displayPlan` / `isPlusOrHigher` come from AuthContext — optimistic (cached) during the
  // auth-load window, real once subscription arrives. Used for the header's plan UI.
  const planDisplay =
    displayPlan === 'Pro'
      ? '🔥 Pro'
      : displayPlan === 'Premium'
        ? '🚀 Premium'
        : displayPlan === 'Plus'
          ? '🎉 Plus'
          : 'Free 🌱'

  const planTextClass =
    displayPlan === 'Pro'
      ? 'text-[#FFB419]'
      : displayPlan === 'Premium' || displayPlan === 'Plus'
        ? 'text-white'
        : 'text-white'

  const toggleMoreContent = () => {
    setIsMoreContentVisible(!isMoreContentVisible)
  }

  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen)
  }

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      router.push(`/search/${searchInput}`)
    }
  }

  const activeButton =
    'text-[#7a74eb] transition duration-100 ease-in-out transition-all duration-100 hover:bg-[var(--foreground-bg-color)] transition-all duration-100 '
  const inactiveButton =
    'text-[#FEFFFF] transition duration-100 ease-in-out hover:bg-[var(--foreground-bg-color)] transition-all duration-100 '

  // Close the large-screen dropdown when clicking anywhere outside the header.
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [dropdownRef])

  // Keep the two account menus mutually exclusive across the md breakpoint: the
  // dropdown only exists at md+, the drawer only below it. If the viewport
  // crosses 768px while one is open, close it so a stale menu can't linger
  // (e.g. an open drawer reappearing on a tablet after a resize).
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return
    const mq = window.matchMedia('(min-width: 768px)')
    const sync = (e) => {
      if (e.matches) {
        setDrawerOpen(false)
      } else {
        setDropdownOpen(false)
      }
    }
    mq.addEventListener('change', sync)
    return () => mq.removeEventListener('change', sync)
  }, [])

  // The settled offer layout uses a badge on phones and a banner on desktop.
  const { variant: offerVariant } = useOfferVariant()
  const activeOffer = useActiveOffer()
  const inUnifiedArm = offerVariant === 'unified' && Boolean(activeOffer)

  // Hide the banner on routes with their own purchase offer.
  const offerRouteAllowed = !(
    pathname.startsWith('/upgrade') ||
    pathname.startsWith('/gems') ||
    pathname.startsWith('/article')
  )

  // Shared avatar button styling (Pro gets the gold gradient border) — reused by
  // the lg+ dropdown trigger and the smaller-screen drawer trigger. `display` is
  // left off here so each trigger can toggle its own (hidden md:flex / flex
  // md:hidden) without the base `flex` overriding `hidden`.
  const avatarButtonClass = `relative font-semibold w-10 h-10 min-w-10 flex-shrink-0 items-center justify-center rounded-full ml-2 overflow-hidden ${
    displayPlan === 'Pro'
      ? 'border border-[1.5px] border-transparent'
      : 'border border-gray-500 bg-transparent'
  }`

  const avatarButtonStyle =
    displayPlan === 'Pro'
      ? {
          background:
            'linear-gradient(var(--background-bg-color), var(--background-bg-color)) padding-box, linear-gradient(135deg, #FFD98A 0%, #F2AF50 40%, #C9852D 100%) border-box',
        }
      : undefined

  const avatarInner = displayProfilePicture ? (
    <img
      src={displayProfilePicture}
      alt={username || 'Profile'}
      className="w-full h-full object-cover object-top rounded-full"
    />
  ) : username ? (
    <span className="w-full h-full flex items-center justify-center bg-[var(--main-bg-color)] text-white uppercase rounded-full font-bold">
      {username.charAt(0)}
    </span>
  ) : (
    <FaUser className="text-white" />
  )

  // 🔄 Use the shared Modal component so the popup is closable (X, backdrop)
  const modal = (
    <Modal
      isOpen={exitModalOpen}
      closeModal={() => setExitModalOpen(false)}
      title=""
      content={<PopUp onClose={() => setExitModalOpen(false)} />}
      width
    />
  )

  return (
    <header
      className="fixed top-0 z-50 bg-[var(--background-bg-color)] shadow-md w-full shadow-xl"
      ref={dropdownRef}
    >
      <nav className="container mx-auto px-2 py-5 items-center">
        <div className="relative flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Link
              href={
                user
                  ? '/explore'
                  : localizedPath('/', getPathLocale(pathname) || i18n.language)
              }
              onClick={() => {
                // Explore defaults to Top; the logged-out landing page still keys its
                // fresh-visitor content off the 'Trending' default.
                setActiveButton(user ? 'Top' : 'Trending')
              }}
              // py/-my cancel out: the hit area is ~36px tall (WCAG 2.5.8)
              // without adding height to the header row.
              className="flex shrink-0 items-center ml-2 py-2 -my-2"
            >
              <img
                src={'/assets/promptchan_logo.svg'}
                alt="Promptchan"
                // Intrinsic size of the SVG — reserves the right box before load
                // (no CLS). Must stay in sync with the file's viewBox, otherwise
                // `preserveAspectRatio` letterboxes the wordmark inside it.
                width="495"
                height="91"
                fetchPriority="high"
                decoding="async"
                // Height-driven, width follows the ratio. Phones span 320–430px,
                // below Tailwind's smallest breakpoint, so the size ramps fluidly
                // instead of stepping at `sm:` (16px on a 320px screen → 20px from
                // ~445px up).
                className="h-[clamp(1rem,4.5vw,1.25rem)] w-auto"
              />
            </Link>

            {/* Search bar, md and up. At md it's a compact fixed width: the
                nav buttons and account controls leave ~139px here, and the
                w-24 nav buttons can't give any back (de/ja labels already fill
                them). Font stays at 16px so iOS doesn't zoom on focus.
                The placeholder would only truncate in the compact md field, so
                it's hidden there and returns at lg. Hidden via colour rather
                than removed: with no visible label, that attribute is the
                input's accessible name and has to hold at every width. */}
            {!safe && (
              <div className="hidden md:block relative">
                <input
                  type="search"
                  name="search"
                  placeholder={t('search')}
                  className="border border-gray-500 rounded-full pl-10 pr-3 py-1.5 w-28 lg:w-5/6 text-white bg-[var(--middle-bg-color)] placeholder:text-transparent lg:placeholder:text-gray-400 focus:border-white focus:outline-none transition-all duration-500 ease-in-out"
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                />
                <span className="absolute left-0 top-3 ml-3.5 text-gray-400">
                  <FaSearch />
                </span>
              </div>
            )}
          </div>

          <div className="absolute inset-y-0 right-0 flex items-center space-x-4 pr-2">
            {/* md+ keeps the full nav in the header row. */}
            <div className="hidden md:flex space-x-1">
              <Link
                style={{ height: '2.6rem' }}
                href="/explore"
                onClick={() => {
                  setActiveButton('Top')
                }}
                className={`${
                  pathname === '/explore' ? activeButton : inactiveButton
                }  font-semibold w-24 text-center py-2 rounded-xl items-center flex justify-center `}
              >
                <BsGridFill className="mr-1.5" />
                <div className="relative text-xs ">{t('explore')}</div>
              </Link>
              <Link
                style={{ height: '2.6rem' }}
                href="/generate"
                className={`${
                  pathname === '/generate' ? activeButton : inactiveButton
                }  font-semibold w-24 text-center py-2 rounded-xl items-center flex justify-center `}
              >
                <FaMagic className="mr-1.5" />
                <div className="relative text-xs ">{t('create')}</div>
              </Link>

              <Link
                style={{ height: '2.6rem' }}
                href="/chat"
                className={`relative ${
                  pathname.startsWith('/chat') ? activeButton : inactiveButton
                }  font-semibold w-24 text-center py-2 rounded-xl items-center flex justify-center `}
              >
                <BsChatDotsFill className="mr-1.5" />
                <div className="relative text-xs ">{t('chat')}</div>
                {showChatNew && (
                  <span className="absolute -top-1.5 -right-1.5 rounded-full bg-[var(--main-bg-color)] px-1.5 py-0.5 text-[9px] font-bold leading-none text-white shadow">
                    {t('new')}
                  </span>
                )}
              </Link>
            </div>

            <div className="flex space-x-2">
              {showAuthed ? (
                <div className="flex items-center justify-center">
                  <GemButton
                    style={{ height: '2.6rem' }}
                    // Badge is arm B only — that's the change being tested.
                    offer={inUnifiedArm ? activeOffer : undefined}
                  />

                  {/* md+: account dropdown trigger. Matches where the nav
                      links live, so tablets get the same header as desktop. */}
                  <button
                    onClick={toggleDropdown}
                    aria-label="Open menu"
                    aria-expanded={dropdownOpen}
                    className={`${avatarButtonClass} hidden md:flex`}
                    style={avatarButtonStyle}
                  >
                    {avatarInner}
                  </button>

                  {/* Phones (< md): hamburger opens the slide-in profile
                      drawer — a menu icon reads more clearly as a menu
                      trigger than the avatar did, and the drawer is the only
                      place the nav links exist at this width. */}
                  <button
                    onClick={() => setDrawerOpen(true)}
                    aria-label="Open menu"
                    aria-expanded={drawerOpen}
                    className="flex md:hidden items-center justify-center w-10 h-10 min-w-10 flex-shrink-0 rounded-full ml-2 border border-gray-500 text-white hover:bg-[var(--secondary-bg-color)] transition-colors"
                  >
                    <FaBars className="text-lg" />
                  </button>

                  {/* Dropdown panel — anchored to the right cluster, matching
                      the original (pre-drawer) placement. */}
                  {dropdownOpen && (
                    <div className="absolute right-0 top-1 mt-10 hidden w-48 overflow-hidden rounded-md bg-[var(--foreground-bg-color)] text-white shadow-lg z-40 md:block lg:mt-12">
                      <div className="px-4 py-4 border-b border-gray-700">
                        <div className="flex items-center gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 min-w-0 mb-1">
                              <Link
                                href={`/profile/${user.uid}`}
                                onClick={toggleDropdown}
                                className="font-semibold text-sm truncate block min-w-0"
                              >
                                {username || user?.uid}
                              </Link>

                              <Link
                                href="/upgrade"
                                onClick={toggleDropdown}
                                className={`text-xs font-semibold whitespace-nowrap ${planTextClass}`}
                              >
                                {planDisplay}
                              </Link>
                            </div>

                            <Link
                              href="/settings"
                              onClick={toggleDropdown}
                              className="text-xs text-gray-300 truncate block"
                            >
                              {email || user?.email || 'No email'}
                            </Link>
                          </div>
                        </div>
                      </div>

                      {isPlusOrHigher ? (
                        <Link
                          href="/gems"
                          className={`flex items-center px-4 py-2 text-sm font-semibold text-white text-right hover:bg-[var(--secondary-bg-color)] ${
                            pathname === '/gems' &&
                            'bg-[var(--secondary-bg-color)]'
                          }`}
                          onClick={toggleDropdown}
                        >
                          <FaGem className="mr-2" />
                          {t('getGems')}
                        </Link>
                      ) : (
                        <Link
                          href="/upgrade"
                          className={`flex items-center px-4 py-2 text-sm font-semibold text-white text-right hover:bg-[var(--secondary-bg-color)] ${
                            pathname === '/upgrade' &&
                            'bg-[var(--secondary-bg-color)]'
                          }`}
                          onClick={toggleDropdown}
                        >
                          <BsRocketTakeoffFill className="mr-2" />
                          {t('upgrade')}
                        </Link>
                      )}

                      {isPlusOrHigher ? (
                        <></>
                      ) : (
                        <Link
                          href={`/earn-gems`}
                          className={`flex items-center px-4 py-2 text-sm font-semibold text-white text-right hover:bg-[var(--secondary-bg-color)] ${
                            pathname.startsWith('/earn-gems') &&
                            'bg-[var(--secondary-bg-color)]'
                          }`}
                          onClick={toggleDropdown}
                        >
                          <FaGem className="mr-2" />
                          {t('dailyReward')}
                        </Link>
                      )}

                      <Link
                        href={`/profile/${user.uid}`}
                        className={`flex items-center px-4 py-2 text-sm font-semibold text-white text-right hover:bg-[var(--secondary-bg-color)] ${
                          pathname.startsWith('/profile/') &&
                          'bg-[var(--secondary-bg-color)]'
                        }`}
                        onClick={toggleDropdown}
                      >
                        <FaHome className="mr-2" />
                        {t('profile')}
                      </Link>

                      <Link
                        href="/settings"
                        className={`flex items-center px-4 py-2 text-sm font-semibold text-white text-right hover:bg-[var(--secondary-bg-color)] ${
                          pathname === '/settings' &&
                          'bg-[var(--secondary-bg-color)]'
                        }`}
                        onClick={toggleDropdown}
                      >
                        <FaCog className="mr-2" />
                        {t('accountSettings')}
                      </Link>

                      <Link
                        href="https://discord.gg/cZuJFFb34e"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center px-4 py-2 text-sm font-semibold text-white text-right hover:bg-[var(--secondary-bg-color)] border-t border-gray-700"
                      >
                        <FaDiscord className="mr-2" />
                        Discord
                      </Link>

                      <Link
                        href={`/contact`}
                        prefetch={false}
                        className="flex items-center px-4 py-2 text-sm font-semibold text-white text-right hover:bg-[var(--secondary-bg-color)]"
                        onClick={toggleDropdown}
                      >
                        <HiMail className="mr-2" />
                        {t('contact')}
                      </Link>

                      <Link
                        href={`/articles`}
                        prefetch={false}
                        className={`flex items-center px-4 py-2 text-sm font-semibold text-white text-right hover:bg-[var(--secondary-bg-color)] ${
                          pathname.startsWith('/articles') &&
                          'bg-[var(--secondary-bg-color)]'
                        }`}
                        onClick={toggleDropdown}
                      >
                        <FaBookOpen className="mr-2" />
                        {t('guide')}
                      </Link>

                      <Link
                        href={`/docs`}
                        prefetch={false}
                        className={`flex items-center px-4 py-2 text-sm font-semibold text-white text-right hover:bg-[var(--secondary-bg-color)] ${
                          pathname.startsWith('/docs') &&
                          'bg-[var(--secondary-bg-color)]'
                        }`}
                        onClick={toggleDropdown}
                      >
                        <FaCode className="mr-2" />
                        {t('API')}
                      </Link>

                      <a
                        href="https://aiffiliate.io"
                        className="flex items-center px-4 py-2 text-sm font-semibold text-white text-right hover:bg-[var(--secondary-bg-color)] border-t border-gray-700"
                        onClick={toggleDropdown}
                        rel="noopener noreferrer"
                      >
                        {/* Gold matches the Pro tier's gradient mid-stop. */}
                        <FaCrown className="mr-2 text-[#F2AF50]" />
                        {t('affiliate')}
                      </a>

                      <button
                        onClick={toggleMoreContent}
                        className="block px-4 py-2 text-sm font-semibold text-white items-center justify-center flex hover:bg-[var(--secondary-bg-color)] w-full"
                      >
                        {isMoreContentVisible ? (
                          <FaChevronUp />
                        ) : (
                          <FaChevronDown />
                        )}
                      </button>
                      {isMoreContentVisible && (
                        <>
                          <Link
                            href="/privacypolicy"
                            prefetch={false}
                            className={`flex items-center px-4 py-2 text-sm font-semibold text-white text-right hover:bg-[var(--secondary-bg-color)] ${
                              pathname === '/privacypolicy' &&
                              'bg-[var(--secondary-bg-color)]'
                            }`}
                            onClick={toggleDropdown}
                          >
                            {t('privacyPolicy')}
                          </Link>
                          <a
                            href="https://promptchan.com/termsofservice"
                            className="flex items-center px-4 py-2 text-sm font-semibold text-white text-right hover:bg-[var(--secondary-bg-color)]"
                            onClick={toggleDropdown}
                            rel="noopener noreferrer"
                          >
                            {t('termsOfService')}
                          </a>
                          <Link
                            href="/refund"
                            prefetch={false}
                            className={`flex items-center px-4 py-2 text-sm font-semibold text-white text-right hover:bg-[var(--secondary-bg-color)] ${
                              pathname === '/refund' &&
                              'bg-[var(--secondary-bg-color)]'
                            }`}
                            onClick={toggleDropdown}
                          >
                            {t('refundpolicy')}
                          </Link>
                          <Link
                            href="/dmcapolicy"
                            prefetch={false}
                            className={`flex items-center px-4 py-2 text-sm font-semibold text-white text-right hover:bg-[var(--secondary-bg-color)] ${
                              pathname === '/dmcapolicy' &&
                              'bg-[var(--secondary-bg-color)]'
                            }`}
                            onClick={toggleDropdown}
                          >
                            {t('dmcapolicy')}
                          </Link>
                          <Link
                            href="/contentmoderationpolicy"
                            prefetch={false}
                            className={`flex items-center px-4 py-2 text-sm font-semibold text-white text-right hover:bg-[var(--secondary-bg-color)] ${
                              pathname.startsWith('/contentmoderationpolicy') &&
                              'bg-[var(--secondary-bg-color)]'
                            }`}
                            onClick={toggleDropdown}
                          >
                            {t('moderationpolicy')}
                          </Link>
                        </>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  href="/signup"
                  aria-label="Sign up for Promptchan"
                  className={`text-white ${
                    pathname === '/signup' &&
                    'bg-[var(--foreground-bg-color)] text-white'
                  } font-semibold lg:w-28 w-24 text-center py-2 rounded-xl ml-4 bg-[var(--main-bg-color)] hover:scale-105 transition-transform duration-200`}
                >
                  {t('signUp')}
                </Link>
              )}
              {!showAuthed && (
                <div className="flex items-center space-x-1">
                  <LanguageSwitcher />
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Second-row nav, phones only (below md) — now for everyone, not just
          anonymous visitors. Signed-in users used to reach Create and Chat
          only through the drawer; giving them this row instead of squeezing
          the links into the row above is what keeps the tap targets full size.
          From md up the same links sit in the header row itself. */}
      <div className="md:hidden flex space-x-2 pb-1 justify-center items-center">
        {/* No search here: it's a lightly used feature, so on phones it lives in
            the side menu rather than spending a slot in the chrome. Signed-out
            visitors lose it entirely at this width, which matches md+ — the
            header input there is gated on `!safe`, and signed-out visitors are
            safe-mode by default. */}
        <div className="flex">
          <Link
            href="/explore"
            onClick={() => {
              setActiveButton('Top')
            }}
            className={`${
              pathname === '/explore' ? activeButton : inactiveButton
            } font-semibold text-center py-2 rounded-2xl flex items-center justify-center flex-col w-16`}
          >
            <BsGridFill className="h-6" />
            <div className="relative text-xs">{t('explore')}</div>
          </Link>
          <Link
            href="/generate"
            className={`${
              pathname === '/generate' ? activeButton : inactiveButton
            } font-semibold text-center py-2 rounded-2xl flex items-center justify-center flex-col w-16`}
          >
            <FaMagic className="h-6" />
            <div className="relative text-xs">{t('create')}</div>
          </Link>

          <Link
            href="/chat"
            className={`relative ${
              pathname.startsWith('/chat') ? activeButton : inactiveButton
            } font-semibold text-center py-2 rounded-2xl flex items-center justify-center flex-col w-16`}
          >
            <BsChatDotsFill className="h-6" />
            <div className="relative text-xs">{t('chat')}</div>
            {showChatNew && (
              <span className="absolute -top-1.5 -right-1.5 rounded-full bg-[var(--main-bg-color)] px-1.5 py-0.5 text-[9px] font-bold leading-none text-white shadow">
                {t('new')}
              </span>
            )}
          </Link>
        </div>
      </div>

      {/* Banners */}
      {(() => {
        if (!offerRouteAllowed) return null

        // Exceptional-state banners need server-verified flags (no safe cached hint),
        // so they wait for the real data to load.
        if (userDataLoaded) {
          if (banned) return <BannedBanner />
          if (ageGate) return <AgeGateBanner />
          if (disputeBanned) return <DisputeBanner />
          if (centroDecline) return <DeclinedBanner />
        }

        // Experiment arm B (phones): the promo moved onto the header CTA as a
        // badge, so the banner carrying the same offer is suppressed. Only
        // these three — the exceptional-state banners above are not offers and
        // run in both arms.
        if (inUnifiedArm) return null

        // DISPLAY ONLY — the plan banner renders from the remembered plan (`displayPlan`)
        // so it doesn't pop in after auth resolves; it self-corrects the moment the real
        // plan loads. Never gates anything (all capabilities read the real subscription).
        if (showAuthed && displayPlan === 'Free') return <DiscountBanner />
        if (showAuthed && displayPlan)
          return <GemsDiscountBanner plan={displayPlan} />

        return null
      })()}

      {pathname !== '/signup' && modal}

      <ProfileDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
    </header>
  )
}

export default Header
